import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  MapPin, Play, Square, Plus, TrendingUp, DollarSign, Clock, 
  ChevronRight, Calendar, Download, Navigation, Car, Briefcase,
  Home, FileText, Settings, Zap, Target, Award, Maximize2, Minimize2,
  Users, Eye, Radio, History, Pause, SkipForward, SkipBack, FastForward, RotateCcw, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { generateMileageReportPDF } from '../utils/PDFGenerator';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { IconTile, TINT } from '../components/ui/IconTile';
import {
  saveTripSnapshot,
  loadLatestTripSnapshot,
  clearTripSnapshot,
} from '../utils/tripSnapshotDB';
import {
  getIrsMileageRate,
  getIrsMileageRateYear,
  isIrsRateProvisional,
} from '../utils/irsMileageRate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom car icon for current position
const carIcon = new L.DivIcon({
  className: 'car-marker',
  html: `<div style="
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #0073ea 0%, #005bb5 100%);
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Create car icon with color
const createCarIcon = (color = '#0073ea', label = '') => new L.DivIcon({
  className: 'fleet-car-marker',
  html: `<div style="
    position: relative;
    width: 44px;
    height: 44px;
  ">
    <div style="
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%);
      border-radius: 50%;
      border: 3px solid #fff;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
        <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
      </svg>
    </div>
    ${label ? `<div style="
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      background: ${color};
      color: white;
      font-size: 10px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 4px;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    ">${label}</div>` : ''}
  </div>`,
  iconSize: [44, 52],
  iconAnchor: [22, 22],
});

// Colors for different vehicles
const vehicleColors = ['#0073ea', '#00c875', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Start marker icon
const startIcon = new L.DivIcon({
  className: 'start-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: #00c875;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component to recenter map
const MapRecenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  return null;
};

const MileageTracker = () => {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [trips, setTrips] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [mileageRate, setMileageRate] = useState(() => getIrsMileageRate());
  const rateYear = getIrsMileageRateYear();
  const rateIsProvisional = isIrsRateProvisional();
  const [showSettings, setShowSettings] = useState(false);
  const [trackingData, setTrackingData] = useState({
    distance: 0,
    duration: 0,
    currentSpeed: 0,
    startAddress: '',
    currentAddress: ''
  });
  const [stats, setStats] = useState({
    totalMiles: 0,
    businessMiles: 0,
    totalDeduction: 0,
    thisWeek: 0,
    thisMonth: 0,
    tripsThisWeek: 0
  });
  
  // Employee data
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  
  const watchIdRef = useRef(null);
  const lastPositionRef = useRef(null);
  const startTimeRef = useRef(null);
  const routePointsRef = useRef([]);
  const currentTripRef = useRef(null);
  const trackingDistanceRef = useRef(0);
  const wakeLockRef = useRef(null);
  const noSleepAudioRef = useRef(null);
  
  const [manualTrip, setManualTrip] = useState({
    startLocation: '',
    endLocation: '',
    miles: '',
    purpose: '',
    date: new Date().toISOString().split('T')[0],
    type: 'business'
  });

  // Map state
  const [mapExpanded, setMapExpanded] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  
  // Fleet tracking state
  const [showFleetView, setShowFleetView] = useState(false);
  const [fleetLocations, setFleetLocations] = useState([]);
  const [fleetMapCenter, setFleetMapCenter] = useState([39.8283, -98.5795]); // US center
  const fleetPollRef = useRef(null);
  
  // Trip History/Playback state
  const [showTripHistory, setShowTripHistory] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackRef = useRef(null);
  
  // Check if user is manager or admin
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  const isEmployee = user?.role === 'employee';
  
  // Get current employee info (logged in user)
  const getCurrentEmployeeInfo = () => {
    // First check if user is from secure auth (backend)
    if (user?.name) {
      return {
        id: user.id || user.email,
        name: user.name,
        email: user.email,
        role: user.role || 'employee'
      };
    }
    
    // Fallback to localStorage employees
    const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    const currentUser = localStorage.getItem('maidly_current_user');
    
    if (currentUser) {
      try {
        const parsed = JSON.parse(currentUser);
        const employee = employees.find(e => 
          e.username === parsed.username || e.email === parsed.email
        );
        if (employee) {
          return {
            id: employee.id,
            name: employee.name,
            email: employee.email,
            role: employee.role || 'employee'
          };
        }
      } catch (e) {
        console.warn('[MileageTracker] Failed to parse current user:', e);
      }
    }
    
    return {
      id: 'unknown',
      name: 'Unknown Driver',
      email: '',
      role: 'employee'
    };
  };
  
  // Get device ID for fleet tracking
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('maidly_device_id');
    if (!deviceId) {
      deviceId = 'device-' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('maidly_device_id', deviceId);
    }
    return deviceId;
  };
  
  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows';
    return 'Device';
  };

  // Common trip purposes for quick selection
  const commonPurposes = [
    { label: 'Client Visit', icon: Briefcase },
    { label: 'Supply Run', icon: Car },
    { label: 'Job Site', icon: Target },
    { label: 'Meeting', icon: Award }
  ];

  useEffect(() => {
    loadData();
    loadSettings();
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (fleetPollRef.current) {
        clearInterval(fleetPollRef.current);
      }
      releaseWakeLock();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fleet view polling
  useEffect(() => {
    if (showFleetView) {
      fetchFleetLocations();
      fleetPollRef.current = setInterval(fetchFleetLocations, 3000); // Poll every 3 seconds
    } else {
      if (fleetPollRef.current) {
        clearInterval(fleetPollRef.current);
      }
    }
    
    return () => {
      if (fleetPollRef.current) {
        clearInterval(fleetPollRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFleetView]);

  // Fetch fleet locations from backend
  const fetchFleetLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fleet/locations`);
      if (response.data.success) {
        setFleetLocations(response.data.locations || []);
        
        // Center map on first location if available
        if (response.data.locations?.length > 0 && !fleetMapCenter) {
          const first = response.data.locations[0];
          setFleetMapCenter([first.latitude, first.longitude]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch fleet locations:', error);
    }
  };

  // Send location update to fleet tracking
  const sendFleetUpdate = async (lat, lon, speed, miles) => {
    try {
      const employeeInfo = getCurrentEmployeeInfo();
      await axios.post(`${API_URL}/api/fleet/update-location`, {
        device_id: getDeviceId(),
        device_name: getDeviceName(),
        driver_name: employeeInfo.name,
        employee_id: employeeInfo.id,
        employee_email: employeeInfo.email,
        latitude: lat,
        longitude: lon,
        speed: speed || 0,
        miles: miles || 0,
        trip_id: currentTrip?.id,
        status: 'tracking'
      });
    } catch (error) {
      // Silent fail - don't interrupt tracking
      console.error('Fleet update error:', error);
    }
  };

  // Stop fleet tracking
  const stopFleetTracking = async () => {
    try {
      await axios.post(`${API_URL}/api/fleet/stop-tracking/${getDeviceId()}`);
    } catch (error) {
      console.error('Fleet stop error:', error);
    }
  };

  // ============================================================
  // KEEP-AWAKE: Screen Wake Lock + silent audio fallback
  // Prevents phone from sleeping / browser from suspending tracking.
  // ============================================================
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('[Mileage] Wake lock released by system');
        });
        console.log('[Mileage] Screen wake lock acquired');
      } else {
        console.warn('[Mileage] Wake Lock API not supported on this device');
      }
    } catch (err) {
      console.warn('[Mileage] Wake lock request failed:', err);
    }

    // Silent audio loop keeps iOS/Safari tab from being fully suspended
    try {
      if (!noSleepAudioRef.current) {
        const audio = new Audio(
          // 1-second silent WAV (base64)
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAiBUAABAsAAACABAAZGF0YQAAAAA='
        );
        audio.loop = true;
        audio.volume = 0.01;
        // best-effort; may reject without user gesture (already have one from Start button)
        await audio.play().catch(() => {});
        noSleepAudioRef.current = audio;
      }
    } catch (e) {
      // Non-fatal
    }
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('[Mileage] Wake lock released');
      }
    } catch (e) {
      // Non-fatal
    }
    try {
      if (noSleepAudioRef.current) {
        noSleepAudioRef.current.pause();
        noSleepAudioRef.current.src = '';
        noSleepAudioRef.current = null;
      }
    } catch (e) {
      // Non-fatal
    }
  };

  // Re-acquire wake lock when user returns to the app
  // (browsers auto-release it when the tab is backgrounded)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isTracking && !wakeLockRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isTracking]);

  // Trip History/Playback functions
  const openTripHistory = (trip) => {
    if (!trip.routePoints || trip.routePoints.length === 0) {
      toast.error('No route data available for this trip');
      return;
    }
    setSelectedTrip(trip);
    setPlaybackIndex(0);
    setIsPlayingBack(false);
    setShowTripHistory(true);
  };

  const startPlayback = () => {
    if (!selectedTrip?.routePoints) return;
    setIsPlayingBack(true);
  };

  const pausePlayback = () => {
    setIsPlayingBack(false);
    if (playbackRef.current) {
      clearInterval(playbackRef.current);
      playbackRef.current = null;
    }
  };

  const resetPlayback = () => {
    pausePlayback();
    setPlaybackIndex(0);
  };

  const skipToEnd = () => {
    pausePlayback();
    if (selectedTrip?.routePoints) {
      setPlaybackIndex(selectedTrip.routePoints.length - 1);
    }
  };

  // Playback animation effect
  useEffect(() => {
    if (isPlayingBack && selectedTrip?.routePoints) {
      playbackRef.current = setInterval(() => {
        setPlaybackIndex(prev => {
          if (prev >= selectedTrip.routePoints.length - 1) {
            pausePlayback();
            return prev;
          }
          return prev + 1;
        });
      }, 500 / playbackSpeed); // Adjust speed based on playbackSpeed
    }
    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlayingBack, selectedTrip, playbackSpeed]);

  // Get trips with route data for history view
  const tripsWithRoutes = trips.filter(t => t.routePoints && t.routePoints.length > 0);

  useEffect(() => {
    calculateStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips, mileageRate]);

  useEffect(() => {
    let interval;
    if (isTracking && startTimeRef.current) {
      interval = setInterval(() => {
        const duration = (Date.now() - startTimeRef.current) / 1000;
        setTrackingData(prev => ({ ...prev, duration }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTracking]);

  const getAddressFromCoords = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`,
        { headers: { 'User-Agent': 'MaidlyBusinessManager/1.0' } }
      );
      const data = await response.json();
      
      if (data.address) {
        const { road, house_number, city, town, village, state } = data.address;
        const street = house_number ? `${house_number} ${road}` : road;
        const locality = city || town || village || '';
        return `${street || ''}, ${locality}${state ? ', ' + state : ''}`.replace(/^, /, '');
      }
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    } catch (error) {
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const loadData = async () => {
    const tripsData = JSON.parse(localStorage.getItem('maidly_mileageEntries') || '[]');
    setTrips(tripsData);
    
    // Load employees for manager view
    const employeesData = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    setEmployees(employeesData);
    
    // Detect a paused/interrupted trip and expose it via the resume banner
    // (do NOT silently restart GPS — let the driver confirm)
    const ongoingTrip = localStorage.getItem('maidly_currentTrip');
    if (ongoingTrip) {
      try {
        const trip = JSON.parse(ongoingTrip);
        setCurrentTrip(trip);
        // isTracking stays false → banner renders, Start button stays visible
      } catch (error) {
        localStorage.removeItem('maidly_currentTrip');
      }
      return;
    }

    // Crash recovery: localStorage may have been cleared, but IndexedDB
    // still holds the last GPS snapshot. Surface it via the resume banner.
    try {
      const snap = await loadLatestTripSnapshot();
      const savedTrips = JSON.parse(localStorage.getItem('maidly_mileageEntries') || '[]');
      const alreadySaved = snap && savedTrips.some((t) => t.id === snap.id);
      if (snap && !alreadySaved) {
        setCurrentTrip(snap);
        toast.info(`Recovered unfinished drive — ${(snap.miles || 0).toFixed(2)} mi so far`, {
          duration: 6000,
        });
      } else if (snap && alreadySaved) {
        // Trip was already completed — drop the stale snapshot
        clearTripSnapshot(snap.id);
      }
    } catch (e) {
      // silent — snapshot recovery is best-effort
    }
  };

  // Warn drivers if their phone is on low battery / likely to enter
  // Low Power Mode, which suspends GPS and background JS on iOS/Android.
  const checkBatterySaverWarning = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.getBattery) {
        const bat = await navigator.getBattery();
        const pct = Math.round(bat.level * 100);
        if (!bat.charging && pct <= 20) {
          toast.warning(
            `Battery at ${pct}% — plug in your phone. Low Power Mode may pause GPS tracking mid-trip.`,
            { duration: 9000 }
          );
        } else if (!bat.charging && pct <= 35) {
          toast.info(
            `Battery at ${pct}%. For a long trip, plug in to avoid Low Power Mode.`,
            { duration: 6000 }
          );
        }
      }
      // iOS Safari doesn't expose Battery API; passive hint stays in the Start screen copy.
    } catch (e) {
      // silent — battery check is best-effort
    }
  };

  // Resume a previously interrupted trip from localStorage
  const resumeInterruptedTrip = () => {
    if (!currentTrip) return;
    if (!navigator.geolocation) {
      toast.error('GPS not supported on this device');
      return;
    }

    const trip = currentTrip;
    const routePts = trip.routePoints || [];
    const startTs = new Date(trip.startTime).getTime();

    // Restore refs
    startTimeRef.current = startTs;
    routePointsRef.current = routePts;
    currentTripRef.current = trip;
    trackingDistanceRef.current = trip.miles || 0;
    lastPositionRef.current = routePts.length
      ? { lat: routePts[routePts.length - 1].lat, lon: routePts[routePts.length - 1].lon }
      : trip.startCoords;

    // Restore visible state
    setRoutePath(routePts.map((p) => [p.lat, p.lon]));
    if (lastPositionRef.current) {
      setCurrentPosition([lastPositionRef.current.lat, lastPositionRef.current.lon]);
    }
    setTrackingData({
      distance: trip.miles || 0,
      duration: (Date.now() - startTs) / 1000,
      currentSpeed: 0,
      startAddress: trip.startLocation || '',
      currentAddress: trip.startLocation || '',
    });
    setIsTracking(true);

    // Restart GPS watch + wake lock
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    requestWakeLock();

    toast.success(`Trip resumed at ${(trip.miles || 0).toFixed(2)} miles`);
  };

  // Discard the paused trip without saving
  const discardInterruptedTrip = () => {
    if (!currentTrip) return;
    const tripId = currentTrip.id;
    localStorage.removeItem('maidly_currentTrip');
    clearTripSnapshot(tripId);
    setCurrentTrip(null);
    currentTripRef.current = null;
    startTimeRef.current = null;
    lastPositionRef.current = null;
    routePointsRef.current = [];
    trackingDistanceRef.current = 0;
    setRoutePath([]);
    setCurrentPosition(null);
    toast.success('Drive discarded');
  };

  const loadSettings = () => {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    // Only honor the saved rate when the user opted for a custom override.
    // Otherwise, use the current-year IRS rate so the app stays up-to-date
    // as the IRS publishes new rates each January.
    if (settings.mileageRateCustom && settings.mileageRate) {
      setMileageRate(settings.mileageRate);
    } else {
      setMileageRate(getIrsMileageRate());
    }
  };

  const calculateStats = () => {
    const now = new Date();
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    weekStart.setHours(0, 0, 0, 0);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const businessTrips = trips.filter(t => t.type === 'business');
    const totalMiles = trips.reduce((sum, t) => sum + (t.miles || 0), 0);
    const businessMiles = businessTrips.reduce((sum, t) => sum + (t.miles || 0), 0);
    const totalDeduction = businessMiles * mileageRate;
    
    const thisWeekTrips = trips.filter(t => new Date(t.date) >= weekStart);
    const thisWeek = thisWeekTrips.filter(t => t.type === 'business').reduce((sum, t) => sum + (t.miles || 0), 0);
    const thisMonth = trips
      .filter(t => new Date(t.date) >= monthStart && t.type === 'business')
      .reduce((sum, t) => sum + (t.miles || 0), 0);
    
    setStats({ 
      totalMiles, 
      businessMiles, 
      totalDeduction, 
      thisWeek,
      thisMonth, 
      tripsThisWeek: thisWeekTrips.length 
    });
  };

  const startTracking = () => {
    if (!navigator.geolocation) {
      toast.error('GPS not supported on this device');
      return;
    }

    // Fire-and-forget: warn about low battery / power-saver mode
    checkBatterySaverWarning();

    toast.loading('Getting your location...', { id: 'gps' });
    
    const employeeInfo = getCurrentEmployeeInfo();

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        // Set initial accuracy
        setGpsAccuracy(Math.round(accuracy));
        
        // Warn if accuracy is poor
        if (accuracy > 100) {
          toast.warning(`GPS accuracy is low (${Math.round(accuracy)}m). Move to an open area for better signal.`, { id: 'gps-accuracy', duration: 5000 });
        }
        
        const address = await getAddressFromCoords(latitude, longitude);
        
        // Initialize map position
        setCurrentPosition([latitude, longitude]);
        setRoutePath([[latitude, longitude]]);
        
        const newTrip = {
          id: Date.now().toString(),
          startTime: new Date().toISOString(),
          startLocation: address,
          startCoords: { lat: latitude, lon: longitude },
          miles: 0,
          date: new Date().toISOString().split('T')[0],
          employeeId: employeeInfo.id,
          employeeName: employeeInfo.name,
          employeeEmail: employeeInfo.email,
          routePoints: [{ lat: latitude, lon: longitude, timestamp: Date.now() }]
        };
        
        localStorage.setItem('maidly_currentTrip', JSON.stringify(newTrip));
        saveTripSnapshot(newTrip);
        
        setCurrentTrip(newTrip);
        currentTripRef.current = newTrip;
        setIsTracking(true);
        startTimeRef.current = Date.now();
        lastPositionRef.current = { lat: latitude, lon: longitude };
        routePointsRef.current = [{ lat: latitude, lon: longitude, timestamp: Date.now() }];
        
        setTrackingData({
          distance: 0,
          duration: 0,
          currentSpeed: 0,
          startAddress: address,
          currentAddress: address
        });
        
        watchIdRef.current = navigator.geolocation.watchPosition(
          handlePositionUpdate,
          handlePositionError,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
        
        // Keep screen awake so tracking survives phone sleep
        requestWakeLock();
        
        toast.success(`Drive started! Tracking as ${employeeInfo.name}`, { id: 'gps' });
      },
      (error) => {
        let errorMsg = 'Could not get location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += 'Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += 'GPS signal unavailable. Try moving to an open area.';
            break;
          case error.TIMEOUT:
            errorMsg += 'GPS took too long. Please try again.';
            break;
          default:
            errorMsg += 'Please enable GPS.';
        }
        toast.error(errorMsg, { id: 'gps' });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handlePositionUpdate = async (position) => {
    const { latitude, longitude, speed, accuracy } = position.coords;
    
    // Update accuracy state for UI feedback
    setGpsAccuracy(Math.round(accuracy));
    
    // Only process if accuracy is acceptable (within 50 meters for better precision)
    // GPS accuracy: < 10m = excellent, < 30m = good, < 50m = acceptable, > 50m = poor
    const ACCURACY_THRESHOLD = 50;
    
    if (accuracy > ACCURACY_THRESHOLD) {
      console.log(`GPS accuracy poor: ${accuracy}m (threshold: ${ACCURACY_THRESHOLD}m)`);
      // Still update position for visualization but don't record for distance
      setCurrentPosition([latitude, longitude]);
      return;
    }
    
    // Update current position for map
    setCurrentPosition([latitude, longitude]);
    
    // Send update to fleet tracking only with good accuracy
    const speedMph = speed ? speed * 2.237 : 0;
    sendFleetUpdate(latitude, longitude, speedMph, trackingDistanceRef.current);
    
    if (lastPositionRef.current && accuracy < ACCURACY_THRESHOLD) {
      const distance = calculateDistance(
        lastPositionRef.current.lat,
        lastPositionRef.current.lon,
        latitude,
        longitude
      );
      
      if (distance > 0.01) {
        routePointsRef.current.push({ lat: latitude, lon: longitude, timestamp: Date.now() });
        
        // Update route path for map
        setRoutePath(routePointsRef.current.map(p => [p.lat, p.lon]));
        
        const totalDistance = routePointsRef.current.reduce((total, point, index) => {
          if (index === 0) return 0;
          const prev = routePointsRef.current[index - 1];
          return total + calculateDistance(prev.lat, prev.lon, point.lat, point.lon);
        }, 0);
        
        setTrackingData(prev => ({
          ...prev,
          distance: totalDistance,
          currentSpeed: speedMph
        }));
        trackingDistanceRef.current = totalDistance;
        
        // Use ref (not state) to avoid the stale-closure captured by watchPosition
        const liveTrip = currentTripRef.current;
        if (liveTrip) {
          const updatedTrip = { ...liveTrip, miles: totalDistance, routePoints: routePointsRef.current };
          localStorage.setItem('maidly_currentTrip', JSON.stringify(updatedTrip));
          currentTripRef.current = updatedTrip;
          setCurrentTrip(updatedTrip);
          // Persistent snapshot for crash/kill recovery (best-effort)
          saveTripSnapshot(updatedTrip);
        }
        
        lastPositionRef.current = { lat: latitude, lon: longitude };
      }
    }
  };

  const handlePositionError = (error) => {
    console.error('Position error:', error);
  };

  const stopTracking = async (tripType = null) => {
    if (!currentTrip) return;
    
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const endAddress = await getAddressFromCoords(latitude, longitude);
        
        const updatedTrip = {
          ...currentTrip,
          endTime: new Date().toISOString(),
          endLocation: endAddress,
          endCoords: { lat: latitude, lon: longitude },
          miles: trackingData.distance,
          duration: trackingData.duration,
          routePoints: routePointsRef.current,
          type: tripType
        };
        
        saveTrip(updatedTrip);
      },
      () => {
        const updatedTrip = {
          ...currentTrip,
          endTime: new Date().toISOString(),
          endLocation: trackingData.currentAddress || currentTrip.startLocation,
          miles: trackingData.distance,
          duration: trackingData.duration,
          routePoints: routePointsRef.current,
          type: tripType
        };
        
        saveTrip(updatedTrip);
      }
    );
  };

  const saveTrip = (trip) => {
    const updated = [...trips, trip];
    setTrips(updated);
    localStorage.setItem('maidly_mileageEntries', JSON.stringify(updated));
    localStorage.removeItem('maidly_currentTrip');
    clearTripSnapshot(trip.id);
    
    setCurrentTrip(null);
    currentTripRef.current = null;
    setIsTracking(false);
    startTimeRef.current = null;
    lastPositionRef.current = null;
    routePointsRef.current = [];
    trackingDistanceRef.current = 0;
    setTrackingData({ distance: 0, duration: 0, currentSpeed: 0, startAddress: '', currentAddress: '' });
    
    // Clear map state
    setCurrentPosition(null);
    setRoutePath([]);
    setMapExpanded(false);
    
    // Stop fleet tracking
    stopFleetTracking();
    
    // Release screen wake lock
    releaseWakeLock();
    
    const deduction = trip.type === 'business' ? (trip.miles * mileageRate).toFixed(2) : '0.00';
    toast.success(
      trip.type 
        ? `Drive saved! ${trip.miles.toFixed(1)} mi = $${deduction} deduction`
        : `Drive saved! Classify it below.`
    );
  };

  const classifyTrip = (tripId, type, purpose = '') => {
    const updated = trips.map(t => 
      t.id === tripId ? { ...t, type, purpose: purpose || t.purpose } : t
    );
    setTrips(updated);
    localStorage.setItem('maidly_mileageEntries', JSON.stringify(updated));
    
    const trip = updated.find(t => t.id === tripId);
    if (type === 'business' && trip) {
      toast.success(`+$${(trip.miles * mileageRate).toFixed(2)} tax deduction!`);
    } else {
      toast.success('Drive classified');
    }
  };

  const deleteTrip = (tripId) => {
    const updated = trips.filter(t => t.id !== tripId);
    setTrips(updated);
    localStorage.setItem('maidly_mileageEntries', JSON.stringify(updated));
    toast.success('Drive deleted');
  };

  const addManualTrip = () => {
    if (!manualTrip.miles || !manualTrip.startLocation) {
      toast.error('Please fill in required fields');
      return;
    }

    const newTrip = {
      id: Date.now().toString(),
      startTime: new Date(manualTrip.date).toISOString(),
      endTime: new Date(manualTrip.date).toISOString(),
      startLocation: manualTrip.startLocation,
      endLocation: manualTrip.endLocation,
      miles: parseFloat(manualTrip.miles),
      type: manualTrip.type,
      purpose: manualTrip.purpose,
      date: manualTrip.date,
      employeeId: user?.id,
      employeeName: user?.name,
      manual: true
    };

    const updated = [...trips, newTrip];
    setTrips(updated);
    localStorage.setItem('maidly_mileageEntries', JSON.stringify(updated));
    
    setShowAddModal(false);
    setManualTrip({
      startLocation: '',
      endLocation: '',
      miles: '',
      purpose: '',
      date: new Date().toISOString().split('T')[0],
      type: 'business'
    });
    
    if (manualTrip.type === 'business') {
      toast.success(`+$${(parseFloat(manualTrip.miles) * mileageRate).toFixed(2)} tax deduction!`);
    } else {
      toast.success('Drive added');
    }
  };

  const exportReport = () => {
    const businessTrips = trips.filter(t => t.type === 'business');
    if (businessTrips.length === 0) {
      toast.error('No business drives to export');
      return;
    }

    const formattedTrips = businessTrips.map(trip => ({
      date: new Date(trip.date).toLocaleDateString(),
      purpose: trip.purpose || 'Business',
      startLocation: trip.startLocation,
      endLocation: trip.endLocation || 'N/A',
      distance: trip.miles,
      notes: ''
    }));

    const totalMiles = businessTrips.reduce((sum, t) => sum + t.miles, 0);
    generateMileageReportPDF(formattedTrips, {
      totalTrips: businessTrips.length,
      totalMiles,
      businessMiles: totalMiles,
      deduction: totalMiles * mileageRate
    });
    
    toast.success('IRS-ready report downloaded!');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const unclassifiedTrips = trips.filter(t => !t.type);
  
  // Apply employee filter first (for managers)
  const employeeFilteredTrips = isManager && employeeFilter !== 'all'
    ? trips.filter(t => t.employeeId === employeeFilter)
    : trips;
  
  const filteredTrips = filter === 'all' 
    ? employeeFilteredTrips 
    : filter === 'unclassified' 
      ? employeeFilteredTrips.filter(t => !t.type)
      : employeeFilteredTrips.filter(t => t.type === filter);

  return (
    <div data-testid="mileage-tracker-page" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      
      {/* Auto-Resume Banner — appears when a paused trip is detected on page load */}
      {currentTrip && !isTracking && (
        <div
          data-testid="mileage-resume-trip-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            borderRadius: 14,
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            border: '1px solid #fdba74',
            boxShadow: '0 4px 14px rgba(249,115,22,0.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
            <div
              style={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(249,115,22,0.4)',
              }}
            >
              <RotateCcw size={22} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#7c2d12', fontSize: '0.95rem' }}>
                Unfinished drive detected
              </div>
              <div style={{ fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.35 }}>
                Started {new Date(currentTrip.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                {' • '}
                <strong>{(currentTrip.miles || 0).toFixed(2)} mi</strong> tracked so far
                {currentTrip.startLocation ? ` from ${currentTrip.startLocation.split(',')[0]}` : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={discardInterruptedTrip}
              data-testid="mileage-resume-discard-btn"
              title="Discard this trip"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.55rem 0.85rem',
                background: '#fff',
                color: '#7c2d12',
                border: '1px solid #fdba74',
                borderRadius: 999,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Trash2 size={14} /> Discard
            </button>
            <button
              onClick={resumeInterruptedTrip}
              data-testid="mileage-resume-trip-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.6rem 1.1rem',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 999,
                fontSize: '0.88rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(249,115,22,0.45)',
                whiteSpace: 'nowrap',
              }}
            >
              <Play size={14} fill="#fff" />
              Resume Drive
            </button>
          </div>
        </div>
      )}
      
      {/* Header with Tax Savings Focus */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <IconTile icon={Car} tint={TINT.mileage} size={44} />
              Mileage Tracker
            </h1>
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {isManager ? 'Manager View - Track all employees' : `Tracking as: ${getCurrentEmployeeInfo().name}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {isManager && (
              <button
                onClick={() => setShowFleetView(true)}
                data-testid="mileage-fleet-view-button"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.55rem 1rem 0.55rem 0.55rem',
                  background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 12,
                  fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 6px 16px rgba(139,92,246,0.35)',
                  transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                  letterSpacing: 0.1,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#7c3aed';
                  e.currentTarget.style.boxShadow = '0 10px 22px rgba(139,92,246,0.55)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#8b5cf6';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(139,92,246,0.35)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconTile icon={Users} tint="rgba(255,255,255,0.22)" size={30} />
                Fleet View
              </button>
            )}

            {/* Add Drive — sky-blue primary (matches Mileage sidebar tint) */}
            <button
              onClick={() => setShowAddModal(true)}
              data-testid="mileage-add-drive-button"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.55rem 1rem 0.55rem 0.55rem',
                background: TINT.mileage, color: '#fff', border: 'none', borderRadius: 12,
                fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 6px 16px ${TINT.mileage}55`,
                transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                letterSpacing: 0.1,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#0284c7';
                e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.mileage}88`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = TINT.mileage;
                e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.mileage}55`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
              Add Drive
            </button>

            {/* Tax Report — green outline. Hidden for employees. */}
            {!isEmployee && (
            <button
              onClick={exportReport}
              data-testid="mileage-tax-report-button"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.55rem 1rem',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                cursor: 'pointer',
                fontSize: '0.9rem', fontWeight: 600, color: '#374151',
                transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#22c55e0d';
                e.currentTarget.style.borderColor = '#22c55e66';
                e.currentTarget.style.color = '#16a34a';
                e.currentTarget.style.boxShadow = '0 6px 16px #22c55e33';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <IconTile icon={FileText} tint="#22c55e" size={30} />
              Tax Report
            </button>
            )}
          </div>
        </div>
        
        {/* Manager: Employee Filter */}
        {isManager && employees.length > 0 && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={18} color="#6b7280" />
                <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Filter by Employee:</span>
              </div>
              <select
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  fontSize: '0.9rem',
                  minWidth: '200px'
                }}
              >
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {employeeFilter === 'all' 
                  ? `Showing ${trips.length} trips from all employees`
                  : `Showing ${trips.filter(t => t.employeeId === employeeFilter).length} trips`
                }
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Tracking Card - Prominent like Stride */}
      <div style={{
        marginBottom: '1.5rem',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0, 115, 234, 0.2)'
      }}>
        {!isTracking ? (
          // Not Tracking - Show big START button
          <div style={{
            background: 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)',
            padding: '2.5rem',
            textAlign: 'center',
            color: '#fff'
          }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '0.5rem' }}>Ready to track your drive?</p>
              <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                IRS {rateYear} Rate: ${mileageRate.toFixed(3)}/mile{rateIsProvisional ? ' (latest published)' : ''} • Auto-tracking with GPS
              </p>
              <p style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: '0.5rem', maxWidth: 340, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.4 }}>
                📱 Keep this app open while driving. We&apos;ll keep your screen awake so tracking doesn&apos;t stop when your phone would normally sleep.
              </p>
              <p style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: '0.35rem', maxWidth: 340, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.4 }}>
                🔋 Turn off <strong>Low Power Mode</strong> — it can pause GPS mid-trip.
              </p>
            </div>
            
            <button
              onClick={startTracking}
              data-testid="start-tracking-button"
              style={{
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #00c875 0%, #00a65a 100%)',
                border: '4px solid rgba(255,255,255,0.3)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 8px 30px rgba(0, 200, 117, 0.4)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 200, 117, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 200, 117, 0.4)';
              }}
            >
              <Play size={48} color="#fff" fill="#fff" />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', marginTop: '0.5rem' }}>
                START
              </span>
            </button>

            <p style={{ marginTop: '1.5rem', fontSize: '0.8rem', opacity: 0.7 }}>
              Tap to begin GPS tracking
            </p>
          </div>
        ) : (
          // Currently Tracking - Show live stats with map
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#fff'
          }}>
            {/* Live Map */}
            <div style={{ 
              height: mapExpanded ? '400px' : '200px', 
              position: 'relative',
              transition: 'height 0.3s ease'
            }}>
              {currentPosition && (
                <MapContainer
                  center={currentPosition}
                  zoom={16}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Route line */}
                  {routePath.length > 1 && (
                    <Polyline 
                      positions={routePath} 
                      color="#00c875" 
                      weight={4}
                      opacity={0.8}
                    />
                  )}
                  
                  {/* Start marker */}
                  {routePath.length > 0 && (
                    <Marker position={routePath[0]} icon={startIcon}>
                      <Popup>Start Point</Popup>
                    </Marker>
                  )}
                  
                  {/* Current position marker */}
                  <Marker position={currentPosition} icon={carIcon}>
                    <Popup>
                      <strong>Current Location</strong><br/>
                      {trackingData.distance.toFixed(2)} miles traveled
                    </Popup>
                  </Marker>
                  
                  <MapRecenter position={currentPosition} />
                </MapContainer>
              )}
              
              {/* Expand/Collapse button */}
              <button
                onClick={() => setMapExpanded(!mapExpanded)}
                style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  zIndex: 1000,
                  padding: '0.5rem',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                {mapExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              
              {/* Live indicator overlay */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                zIndex: 1000,
                padding: '0.5rem 0.75rem',
                background: 'rgba(0,0,0,0.7)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00c875',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>LIVE</span>
              </div>
              
              {/* GPS Accuracy indicator */}
              {gpsAccuracy !== null && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '60px',
                  zIndex: 1000,
                  padding: '0.5rem 0.75rem',
                  background: gpsAccuracy <= 10 ? 'rgba(0,200,117,0.9)' : 
                              gpsAccuracy <= 30 ? 'rgba(0,200,117,0.7)' : 
                              gpsAccuracy <= 50 ? 'rgba(255,193,7,0.9)' : 
                              'rgba(255,87,34,0.9)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Navigation size={12} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                    {gpsAccuracy <= 10 ? 'GPS: Excellent' : 
                     gpsAccuracy <= 30 ? 'GPS: Good' : 
                     gpsAccuracy <= 50 ? 'GPS: OK' : 
                     `GPS: Poor (${gpsAccuracy}m)`}
                  </span>
                </div>
              )}
            </div>

            {/* Stats below map */}
            <div style={{ padding: '1.5rem' }}>
              {/* Distance Display */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '1rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1 }}>
                    {trackingData.distance.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>miles</div>
                </div>
                <div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1, color: '#00c875' }}>
                    ${(trackingData.distance * mileageRate).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>deduction</div>
                </div>
                <div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1 }}>
                    {formatDuration(trackingData.duration)}
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>duration</div>
                </div>
              </div>

              {/* Speed and Location */}
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '10px',
                marginBottom: '1.5rem',
                fontSize: '0.85rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Navigation size={16} />
                  <span>{trackingData.currentSpeed.toFixed(0)} mph</span>
                </div>
                <div style={{ opacity: 0.8, textAlign: 'right', maxWidth: '60%' }}>
                  {trackingData.startAddress?.split(',').slice(0, 2).join(',')}
                </div>
              </div>

              {/* Stop Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => stopTracking('business')}
                  data-testid="stop-business-btn"
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #00c875 0%, #00a65a 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Briefcase size={20} />
                  Business
                </button>
                <button
                  onClick={() => stopTracking('personal')}
                  data-testid="stop-personal-btn"
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Home size={20} />
                  Personal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
          <DollarSign size={24} color="#00c875" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#00c875' }}>
            ${stats.totalDeduction.toFixed(0)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Tax Deduction</div>
        </div>
        
        <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
          <TrendingUp size={24} color="#0073ea" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a1a' }}>
            {stats.businessMiles.toFixed(0)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Business Miles</div>
        </div>
        
        <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
          <Calendar size={24} color="#f59e0b" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a1a' }}>
            {stats.thisWeek.toFixed(0)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>This Week</div>
        </div>
        
        <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
          <Car size={24} color="#8b5cf6" style={{ marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a1a' }}>
            {stats.tripsThisWeek}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Drives</div>
        </div>
      </div>

      {/* Unclassified Alert */}
      {unclassifiedTrips.length > 0 && (
        <div style={{
          padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '1px solid #fbbf24',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Clock size={20} color="#b45309" />
            <span style={{ fontWeight: 600, color: '#92400e' }}>
              {unclassifiedTrips.length} drive{unclassifiedTrips.length > 1 ? 's' : ''} to classify
            </span>
          </div>
          <button
            onClick={() => setFilter('unclassified')}
            style={{
              padding: '0.5rem 1rem',
              background: '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Review
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1rem',
        overflowX: 'auto',
        padding: '0.25rem 0'
      }}>
        {[
          { value: 'all', label: 'All Drives' },
          { value: 'business', label: 'Business' },
          { value: 'personal', label: 'Personal' },
          { value: 'unclassified', label: `Unclassified (${unclassifiedTrips.length})` }
        ].map(f => {
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              data-testid={`mileage-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              style={{
                padding: '9px 18px',
                background: active ? '#0073ea' : '#fff',
                color: active ? '#fff' : '#475569',
                border: `1px solid ${active ? '#0073ea' : '#e5e7eb'}`,
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: active ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.15s, color 0.15s, box-shadow 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = '#fff'; }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Trips List */}
      <div className="card" style={{ padding: '0' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>Recent Drives</h2>
        </div>
        
        {filteredTrips.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <Car size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <p>No drives found</p>
            <p style={{ fontSize: '0.85rem' }}>Start tracking or add a manual drive</p>
          </div>
        ) : (
          <div>
            {filteredTrips.sort((a, b) => new Date(b.date) - new Date(a.date)).map(trip => (
              <div
                key={trip.id}
                style={{
                  padding: '1.25rem 1.5rem',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  {/* Left: Trip Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: trip.type === 'business' ? '#00c875' : trip.type === 'personal' ? '#6b7280' : '#f59e0b'
                      }} />
                      <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {new Date(trip.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      {trip.manual && <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: '#e5e7eb', borderRadius: '4px' }}>Manual</span>}
                      {/* Show employee name for managers */}
                      {isManager && trip.employeeName && (
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.2rem 0.6rem', 
                          background: '#dbeafe', 
                          color: '#1e40af',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {trip.employeeName}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                      {trip.startLocation?.split(',')[0]}
                      {trip.endLocation && (
                        <span style={{ color: '#6b7280' }}> → {trip.endLocation?.split(',')[0]}</span>
                      )}
                    </div>
                    
                    {trip.purpose && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{trip.purpose}</div>
                    )}
                  </div>
                  
                  {/* Right: Miles & Actions */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                      {trip.miles.toFixed(1)} mi
                    </div>
                    
                    {trip.type === 'business' && (
                      <div style={{ fontSize: '0.9rem', color: '#00c875', fontWeight: 600 }}>
                        ${(trip.miles * mileageRate).toFixed(2)}
                      </div>
                    )}
                    
                    {!trip.type && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button
                          onClick={() => classifyTrip(trip.id, 'business')}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: '#00c875',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Business
                        </button>
                        <button
                          onClick={() => classifyTrip(trip.id, 'personal')}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                        >
                          Personal
                        </button>
                      </div>
                    )}
                    
                    {trip.type && (
                      <span style={{
                        display: 'inline-block',
                        marginTop: '0.25rem',
                        padding: '0.25rem 0.75rem',
                        background: trip.type === 'business' ? '#dcfce7' : '#f3f4f6',
                        color: trip.type === 'business' ? '#166534' : '#6b7280',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {trip.type}
                      </span>
                    )}
                    
                    {/* View Route Button */}
                    {trip.routePoints && trip.routePoints.length > 1 && (
                      <button
                        onClick={() => openTripHistory(trip)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          marginTop: '0.5rem',
                          padding: '0.375rem 0.75rem',
                          background: 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        <History size={12} />
                        View Route
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Manual Trip Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Add Drive Manually</h3>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              {/* Quick Purpose Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Quick Select Purpose</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {commonPurposes.map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setManualTrip({ ...manualTrip, purpose: p.label, type: 'business' })}
                      style={{
                        padding: '0.75rem',
                        border: manualTrip.purpose === p.label ? '2px solid #0073ea' : '1px solid #e5e7eb',
                        background: manualTrip.purpose === p.label ? '#eff6ff' : '#fff',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.85rem'
                      }}
                    >
                      <p.icon size={16} color="#0073ea" />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>From *</label>
                <input
                  type="text"
                  value={manualTrip.startLocation}
                  onChange={(e) => setManualTrip({ ...manualTrip, startLocation: e.target.value })}
                  className="input"
                  placeholder="Starting address"
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>To</label>
                <input
                  type="text"
                  value={manualTrip.endLocation}
                  onChange={(e) => setManualTrip({ ...manualTrip, endLocation: e.target.value })}
                  className="input"
                  placeholder="Destination address"
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Miles *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={manualTrip.miles}
                    onChange={(e) => setManualTrip({ ...manualTrip, miles: e.target.value })}
                    className="input"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Date *</label>
                  <input
                    type="date"
                    value={manualTrip.date}
                    onChange={(e) => setManualTrip({ ...manualTrip, date: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Type *</label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => setManualTrip({ ...manualTrip, type: 'business' })}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: manualTrip.type === 'business' ? '2px solid #00c875' : '1px solid #e5e7eb',
                      background: manualTrip.type === 'business' ? '#dcfce7' : '#fff',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Briefcase size={18} color={manualTrip.type === 'business' ? '#166534' : '#6b7280'} />
                    Business
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualTrip({ ...manualTrip, type: 'personal' })}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: manualTrip.type === 'personal' ? '2px solid #6b7280' : '1px solid #e5e7eb',
                      background: manualTrip.type === 'personal' ? '#f3f4f6' : '#fff',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Home size={18} color="#6b7280" />
                    Personal
                  </button>
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Purpose / Notes</label>
                <input
                  type="text"
                  value={manualTrip.purpose}
                  onChange={(e) => setManualTrip({ ...manualTrip, purpose: e.target.value })}
                  className="input"
                  placeholder="Client meeting, supply run, etc."
                />
              </div>

              {/* Estimated Deduction Preview */}
              {manualTrip.miles && manualTrip.type === 'business' && (
                <div style={{
                  padding: '1rem',
                  background: '#dcfce7',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '0.25rem' }}>Estimated Tax Deduction</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#15803d' }}>
                    ${(parseFloat(manualTrip.miles || 0) * mileageRate).toFixed(2)}
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button 
                  onClick={() => setShowAddModal(false)} 
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  onClick={addManualTrip} 
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  Add Drive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Fleet View Modal */}
      {showFleetView && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          {/* Header */}
          <div style={{
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Users size={24} />
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Fleet View</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.7 }}>
                  {fleetLocations.filter(l => l.status === 'tracking').length} active vehicles
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFleetView(false)}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Close
            </button>
          </div>

          {/* Map */}
          <div style={{ flex: 1, position: 'relative' }}>
            {fleetLocations.length > 0 ? (
              <>
              <MapContainer
                center={[fleetLocations[0]?.latitude || 39.8283, fleetLocations[0]?.longitude || -98.5795]}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {fleetLocations.map((location, index) => (
                  <Marker
                    key={location.device_id}
                    position={[location.latitude, location.longitude]}
                    icon={createCarIcon(vehicleColors[index % vehicleColors.length], location.driver_name?.split(' ')[0] || location.device_name)}
                  >
                    <Popup>
                      <div style={{ minWidth: '150px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem' }}>
                          {location.driver_name || location.device_name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          <strong>Device:</strong> {location.device_name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          <strong>Speed:</strong> {location.speed?.toFixed(0) || 0} mph
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          <strong>Miles:</strong> {location.miles?.toFixed(1) || 0}
                        </div>
                        <div style={{ 
                          marginTop: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          background: location.status === 'tracking' ? '#dcfce7' : '#f3f4f6',
                          color: location.status === 'tracking' ? '#166534' : '#6b7280',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'inline-block'
                        }}>
                          {location.status === 'tracking' ? '● Active' : '○ Stopped'}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              
              </>
            ) : (
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}>
                <Radio size={64} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>No Active Vehicles</h3>
                <p style={{ opacity: 0.7, textAlign: 'center', maxWidth: '300px' }}>
                  Start tracking on any device and it will appear here in real-time.
                </p>
              </div>
            )}

            {/* Legend */}
            {fleetLocations.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                background: 'rgba(255,255,255,0.95)',
                borderRadius: '12px',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                maxWidth: '280px',
                maxHeight: '200px',
                overflow: 'auto',
                zIndex: 1000
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#1f2937' }}>
                  Active Vehicles ({fleetLocations.filter(l => l.status === 'tracking').length})
                </div>
                {fleetLocations.map((location, index) => (
                  <div 
                    key={location.device_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0',
                      borderBottom: index < fleetLocations.length - 1 ? '1px solid #f3f4f6' : 'none'
                    }}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: vehicleColors[index % vehicleColors.length]
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {location.driver_name || location.device_name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {location.speed?.toFixed(0) || 0} mph • {location.miles?.toFixed(1) || 0} mi
                      </div>
                    </div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: location.status === 'tracking' ? '#00c875' : '#9ca3af',
                      animation: location.status === 'tracking' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trip History/Playback Modal */}
      {showTripHistory && selectedTrip && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          {/* Header */}
          <div style={{
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <History size={24} />
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Trip Playback</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.7 }}>
                  {selectedTrip.date} • {selectedTrip.miles?.toFixed(2)} miles
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                pausePlayback();
                setShowTripHistory(false);
                setSelectedTrip(null);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Close
            </button>
          </div>

          {/* Map with Route */}
          <div style={{ flex: 1, position: 'relative' }}>
            <MapContainer
              center={[selectedTrip.routePoints[0]?.lat || 39.8283, selectedTrip.routePoints[0]?.lon || -98.5795]}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Full route line (gray) */}
              <Polyline 
                positions={selectedTrip.routePoints.map(p => [p.lat, p.lon])} 
                color="#94a3b8" 
                weight={4}
                opacity={0.5}
              />
              
              {/* Traveled route line (green) */}
              <Polyline 
                positions={selectedTrip.routePoints.slice(0, playbackIndex + 1).map(p => [p.lat, p.lon])} 
                color="#00c875" 
                weight={5}
                opacity={0.9}
              />
              
              {/* Start marker */}
              <Marker 
                position={[selectedTrip.routePoints[0]?.lat, selectedTrip.routePoints[0]?.lon]} 
                icon={startIcon}
              >
                <Popup>
                  <strong>Start</strong><br/>
                  {selectedTrip.startLocation}
                </Popup>
              </Marker>
              
              {/* End marker */}
              <Marker 
                position={[
                  selectedTrip.routePoints[selectedTrip.routePoints.length - 1]?.lat, 
                  selectedTrip.routePoints[selectedTrip.routePoints.length - 1]?.lon
                ]} 
                icon={new L.DivIcon({
                  className: 'end-marker',
                  html: `<div style="
                    width: 24px;
                    height: 24px;
                    background: #ef4444;
                    border-radius: 50%;
                    border: 3px solid #fff;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  "></div>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12],
                })}
              >
                <Popup>
                  <strong>End</strong><br/>
                  {selectedTrip.endLocation}
                </Popup>
              </Marker>
              
              {/* Current playback position */}
              <Marker 
                position={[
                  selectedTrip.routePoints[playbackIndex]?.lat, 
                  selectedTrip.routePoints[playbackIndex]?.lon
                ]} 
                icon={carIcon}
              >
                <Popup>Current Position</Popup>
              </Marker>
              
              <MapRecenter position={[
                selectedTrip.routePoints[playbackIndex]?.lat, 
                selectedTrip.routePoints[playbackIndex]?.lon
              ]} />
            </MapContainer>

            {/* Trip Info Overlay */}
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '12px',
              padding: '1rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              maxWidth: '250px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#1f2937' }}>
                {selectedTrip.employeeName || 'Trip Details'}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                <strong>From:</strong> {selectedTrip.startLocation}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                <strong>To:</strong> {selectedTrip.endLocation || 'In Progress'}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                <strong>Type:</strong> {selectedTrip.type === 'business' ? '💼 Business' : '🏠 Personal'}
              </div>
            </div>

            {/* Progress indicator */}
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'rgba(0,0,0,0.8)',
              color: '#fff',
              borderRadius: '20px',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              zIndex: 1000
            }}>
              Point {playbackIndex + 1} of {selectedTrip.routePoints.length}
            </div>
          </div>

          {/* Playback Controls */}
          <div style={{
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#fff'
          }}>
            {/* Progress Bar */}
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="range"
                min="0"
                max={selectedTrip.routePoints.length - 1}
                value={playbackIndex}
                onChange={(e) => {
                  pausePlayback();
                  setPlaybackIndex(parseInt(e.target.value));
                }}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
            </div>

            {/* Control Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
              <button
                onClick={resetPlayback}
                style={{
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Reset"
              >
                <SkipBack size={20} />
              </button>

              <button
                onClick={isPlayingBack ? pausePlayback : startPlayback}
                style={{
                  padding: '1rem',
                  background: isPlayingBack ? '#ef4444' : '#00c875',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
                title={isPlayingBack ? 'Pause' : 'Play'}
              >
                {isPlayingBack ? <Pause size={24} /> : <Play size={24} fill="#fff" />}
              </button>

              <button
                onClick={skipToEnd}
                style={{
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Skip to End"
              >
                <SkipForward size={20} />
              </button>

              {/* Speed Control */}
              <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FastForward size={16} />
                <select
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                  style={{
                    padding: '0.5rem',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="0.5" style={{ color: '#000' }}>0.5x</option>
                  <option value="1" style={{ color: '#000' }}>1x</option>
                  <option value="2" style={{ color: '#000' }}>2x</option>
                  <option value="4" style={{ color: '#000' }}>4x</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MileageTracker;
